import { Box, Breadcrumbs, Container, Link as MatLink, Typography } from '@mui/material';
import { UnregisterCallback } from 'history';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link, RouteComponentProps } from 'react-router-dom';

import { RequireRole } from 'app/components/require-role';
import { Role } from 'app/constants/role';
import { LatestDataDate } from 'features/tools/latest-data-date';

interface CrumbMap {
    name: string;
    // if true the next section of the url will be treated as id variable
    hasVar?: boolean;
    children: Record<string, CrumbMap>;
}

const map: Record<string, CrumbMap> = {
    '': {
        name: 'Home',
        children: {
            'login': {
                name: 'Login',
                children: {},
            },
            'seam': {
                name: 'SEAM',
                children: {
                    'dashboard': {
                        name: 'Dashboard',
                        children: {},
                    },
                    'kanban': {
                        name: 'Kanban',
                        children: {},
                    },
                    'request': {
                        hasVar: true,
                        name: 'Request',
                        children: {
                            details: {
                                name: 'Details',
                                children: {},
                            },
                            overview: {
                                name: 'Overview',
                                children: {},
                            },
                            qa: {
                                name: 'Data Quality',
                                children: {},
                            },
                            labels: {
                                name: 'Labels',
                                children: {},
                            },
                            patterns: {
                                name: 'Patterns',
                                children: {},
                            },
                        },
                    },
                    'metrics': {
                        name: 'Metrics',
                        children: {},
                    },
                    '*': {
                        name: 'Details',
                        children: {},
                    },
                },
            },
            'cris': {
                name: 'CRIS',
                children: {
                    'list': {
                        name: 'List',
                        children: {},
                    },
                    'request': {
                        name: 'Request',
                        hasVar: true,
                        children: {
                            actions: {
                                name: 'Actions',
                                children: {},
                            },
                        },
                    },
                    'kanban': {
                        name: 'Kanban',
                        children: {},
                    },
                    'stores': {
                        name: 'Store',
                        hasVar: true,
                        children: {
                            details: {
                                name: 'Details',
                                children: {},
                            },
                            edit: {
                                name: 'Edit',
                                children: {},
                            },
                            delete: {
                                name: 'Delete',
                                children: {},
                            },
                        },
                    },
                    '*': {
                        name: 'Details',
                        children: {},
                    },
                },
            },
            'qa': {
                name: 'QA',
                children: {
                    'impact': {
                        name: 'Impact Report',
                        children: {},
                    },
                    'f-score': {
                        name: 'F-Score Report',
                        children: {},
                    },
                },
            },
            'crowd': {
                name: 'Crowdsource',
                children: {},
            },
            'users': {
                name: 'Users',
                children: {
                    '*': {
                        name: 'Details',
                        children: {},
                    },
                },
            },
            'explore': {
                name: 'Explore',
                children: {
                    search: {
                        name: 'Search',
                        children: {},
                    },
                    tuples: {
                        name: 'Tuples',
                        children: {},
                    },
                    patterns: {
                        name: 'Patterns',
                        children: {},
                    },
                },
            },
            'tools': {
                name: 'Tools',
                children: {
                    health: {
                        name: 'Health',
                        children: {},
                    },
                },
            },
            'ml-versions': {
                name: 'ML Versions',
                children: {},
            },
        },
    },
};

interface AppBreadCrumbState {
    path: string;
}

class AppBreadCrumbs extends React.Component<RouteComponentProps, AppBreadCrumbState> {
    private unsubscribeHistory?: UnregisterCallback;

    public constructor(props: Readonly<RouteComponentProps> | RouteComponentProps) {
        super(props);

        this.state = {
            path: this.props.history.location.pathname,
        };
    }

    public componentDidMount(): void {
        this.unsubscribeHistory = this.props.history.listen((location) => {
            this.setState({ path: location.pathname });
        });
    }

    public componentWillUnmount(): void {
        if (this.unsubscribeHistory) {
            this.unsubscribeHistory();
        }
    }

    public render(): React.ReactNode {
        const bc = [];
        const parts = this.state.path.split('/');
        let url = '';
        let currentMap = map;
        let lastItem: CrumbMap | undefined;

        for (let i = 0; i < parts.length - 1; i++) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            lastItem = currentMap[parts[i]] ?? currentMap['*'];
            const nextPartIndex = i + 1 + (lastItem.hasVar ? 1 : 0);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const nextChildren = lastItem.children[parts[nextPartIndex]] ?? lastItem.children['*'];

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!nextChildren) {
                break;
            }

            if (0 !== i) {
                // do not show home

                url += `/${parts[i]}`;
                if (lastItem.hasVar) {
                    url += `/${parts[i + 1]}`;
                    i++;
                }
                bc.push(
                    <MatLink key={i} component={Link} color='primary' to={url} underline='hover'>
                        {lastItem.name}
                    </MatLink>,
                );
            }

            currentMap = lastItem.children;
            lastItem = nextChildren;
        }

        if (lastItem && 'Home' !== lastItem.name) {
            bc.push(
                <Typography key={-1} color='textPrimary'>
                    {lastItem.name}
                </Typography>,
            );
        }

        return (
            <div style={{ paddingBottom: '0.3rem' }}>
                <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Breadcrumbs aria-label='breadcrumb'>{bc}</Breadcrumbs>
                    <Box sx={{ flexGrow: 1 }} />
                    <RequireRole roles={Role.USER}>
                        <LatestDataDate />
                    </RequireRole>
                </Container>
            </div>
        );
    }
}

const connected = connect()(withRouter(AppBreadCrumbs));

export { connected as AppBreadCrumbs };
